<template lang="pug">
AppPopover(
  ref="AppPopover"
  placement="right"
  extra-popover-classes="fc-event-container"
  boundaries-element="scrollParent"
  lazy
)
  template(#body)
    .timesheet-event-popover-head
      .popover-head-left
        .project-cell-wrapper
          AppAvatarInitial.valign-top(:text="timeEntry.project.text")
          .time-entry-data.inline-block
            .project
              .inline-block
                .name.strong
                  | {{ timeEntry.project.text }}
            strong.hi-10.org-name.is-uppercase.text-lighter
              | {{ timeEntry.organization.text }}
            .task
              .inline-block
                .name.strong.small
                  | {{ taskName }}
      .popover-head-right
        .small.strong
          | {{ formattedRange }}
        .small.text-lighter
          | {{ formatDate(timeEntry.start_time) }}
    table.table-condensed.table-borderless.table
      tbody
        tr
          td.strong.no-wrap Duration:
          td
            .inline-block
              data.duration
                |
                | {{ formatDuration(timeEntry.tracked) }}
              |
              | hrs
            .manual-time.inline-block(v-if="timeEntry.work_break") Work break
            .manual-time.inline-block(v-else)
              | {{ numberToPercentage(timeEntry.manual) }}
              |
              | manual
            i.fa.fa-dollar.a-tooltip.ml-5(
              v-if="timeEntry.billable"
              v-tooltip="{ placement: 'right' }"
              title="Billable time"
            )
            TimeEntriesLockedPopover(
              v-if="showLocked(timeEntry)"
              trigger="hover"
              :reasons="timeEntry.allReasons"
            )
              template(#trigger)
                .inline-block
                  i.hi.hi-lock.ml-5
        tr(v-if="reasonsArePresent")
          td.strong.no-wrap {{ reasonString }}:
          td
            .text-overflow(
              v-for="reason in timeEntry.reasons"
              :key="reason.note"
            )
              | {{ stripHTML(reason.note) }}
    .btn-group-spaced.is-justified(v-if="!showLocked(timeEntry)")
      a.btn.btn-sm.btn-danger.pull-left(
        v-tooltip="{ placement: 'bottom', html: true }"
        data-container="body"
        :title="deleteTimeEntryTooltip"
        :disabled="(preventOwnTimeDeletion || timeEntry.manual_time_request) ? 'disabled' : null"
        @click="openDialog('delete')"
      ) Delete
      a.btn.btn-sm.btn-primary.pull-right(
        v-tooltip="{ placement: 'bottom', html: true }"
        data-container="body"
        :disabled="editDisabled ? 'disabled' : null"
        :data-original-title="timeEntry.modifiableTooltip"
        @click="openDialog('edit')"
      ) Edit
      a.btn.btn-sm.btn-default.pull-right(
        v-if="!timeEntry.work_break"
        v-tooltip="{ placement: 'bottom', html: true }"
        data-container="body"
        :disabled="editDisabled ? 'disabled' : null"
        :data-original-title="timeEntry.modifiableTooltip"
        @click="openDialog('split')"
      ) Split
  template(#trigger)
    slot(name="trigger")
</template>

<script>
import { isEmpty } from 'lodash-es';
import { formatDatetimeRange } from '~/utils/time_utils';
import { Time, Html } from '~/filters';
import AppPopover from '~/components/AppPopover.vue';
import AppAvatarInitial from '~/components/AppAvatarInitial.vue';
import tooltip from '~/directives/tooltip';
// TODO (unlocking time): Use the popover for the cases where time can't get changed.
import TimeEntriesLockedPopover from '../TimeEntriesLockedPopover.vue';

export default {
  name: 'TimeEntriesCalendarEventPopover',
  components: {
    TimeEntriesLockedPopover,
    AppPopover,
    AppAvatarInitial,
  },
  directives: {
    tooltip,
  },
  // FullCalendar renders this in an isolated app with no Vuex store; the store is
  // provided by TimeEntriesCalendarEvent (which AppTimeGrid handed it to via a prop).
  inject: ['calendarStore'],
  props: {
    timeEntry: {
      type: Object,
      required: true,
    },
  },
  computed: {
    showLocked() {
      return this.calendarStore.getters.showLocked;
    },
    preventOwnTimeDeletion() {
      return this.calendarStore.state.prevent_own_time_deletion;
    },
    reasonsArePresent() {
      return !isEmpty(this.timeEntry.reasons);
    },
    hasMultipleReasons() {
      return this.timeEntry.reasons.length > 1;
    },
    reasonString() {
      return this.hasMultipleReasons ? 'Reasons' : 'Reason';
    },
    formattedRange() {
      return formatDatetimeRange(this.timeEntry.start_time, this.timeEntry.stop_time, { style: 'time' });
    },
    taskName() {
      const task = this.timeEntry.task;

      return task ? task.text : '';
    },
    editDisabled() {
      return this.timeEntry.editDisabled;
    },
    deleteTimeEntryTooltip() {
      return this.preventOwnTimeDeletion ? 'You do not have permission to delete time' : null;
    },
  },
  methods: {
    formatDate: Time.formatDate,
    formatDuration: Time.formatDuration,
    stripHTML: Html.stripHTML,
    numberToPercentage(num) {
      return ((num / this.timeEntry.tracked) * 100).toFixed(0) + '%';
    },
    openDialog(type) {
      const openEdit = (type === 'edit' || type === 'split') && !this.editDisabled;
      const openDelete = type === 'delete' && !this.preventOwnTimeDeletion && !this.timeEntry.manual_time_request;

      if (openEdit || openDelete) {
        this.$refs.AppPopover.close();
        this.$emit('open-dialog', { type, timeEntryId: this.timeEntry.id });
      }
    },
  },
};
</script>
